import { api } from "@/lib/axiosClient";

// ── Типы (зеркалят backend ArenaDtos, camelCase) ─────────────────────────────

export interface ArenaPlacement {
  tournamentTeamId: string;
  teamName: string;
  teamTag?: string | null;
  placement: number;
  pointsAwarded: number;
  source: string;
}

export interface ArenaPlayerStat {
  puuid: string;
  riotIdGameName?: string | null;
  riotIdTagLine?: string | null;
  subteamPlacement: number;
  playerSubteamId: number;
  championId: number;
  champLevel: number;
  spell1Id: number;
  spell2Id: number;
  kills: number;
  deaths: number;
  assists: number;
  goldEarned: number;
  goldSpent: number;
  items: number[];
  augments: number[];
  totalDamageDealtToChampions: number;
  physicalDamageDealtToChampions: number;
  magicDamageDealtToChampions: number;
  trueDamageDealtToChampions: number;
  largestCriticalStrike: number;
  totalDamageTaken: number;
  damageSelfMitigated: number;
  totalHeal: number;
  timeCCingOthers: number;
  largestKillingSpree: number;
  largestMultiKill: number;
  doubleKills: number;
  tripleKills: number;
  quadraKills: number;
  pentaKills: number;
  totalMinionsKilled: number;
  visionScore: number;
  tournamentTeamId?: string | null;
  teamName?: string | null;
}

export interface ArenaGame {
  id: string;
  gameNumber: number;
  status: "Pending" | "Live" | "Completed" | "Cancelled";
  lobbyHostUserId?: string | null;
  riotGameId?: string | null;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
  placements: ArenaPlacement[];
  players: ArenaPlayerStat[];
}

export interface ArenaStanding {
  rank: number;
  tournamentTeamId: string;
  teamName: string;
  teamTag?: string | null;
  totalPoints: number;
  gamesPlayed: number;
  firstPlaces: number;
  secondPlaces: number;
  placementsByGame: number[];
}

export interface ArenaSeries {
  tournamentId: string;
  teamCount: number;
  gamesCount: number;
  /** place → points */
  scoring: Record<string, number>;
  games: ArenaGame[];
  standings: ArenaStanding[];
}

export interface ArenaPlacementInput {
  tournamentTeamId: string;
  placement: number;
}

// ── Arena Random: сборка троек ───────────────────────────────────────────────

export interface ArenaAssemblyMember {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  isCaptain: boolean;
  /** Приглашён «извне» (вступил по инвайт-ссылке, аккаунт мог быть авто-создан). */
  external?: boolean;
  /** Riot game name основного аккаунта (для плиток состава). */
  riotAccountName?: string | null;
  /** Riot tag line основного аккаунта. */
  riotAccountTag?: string | null;
  /** Регион аккаунта (RU/EUW1/…) — для корректной op.gg-ссылки. */
  riotAccountRegion?: string | null;
  /** Текущий ранг основного Riot-аккаунта. */
  currentRank?: string | null;
}

export interface ArenaRosterInvite {
  rosterId: string;
  token: string;
  invitePath: string;
}

export interface ArenaRosterAudit {
  rosterId: string;
  who: string;
  what: string;
  whenUtc: string;
}

export interface ArenaAssemblyTeam {
  rosterId: string;
  name: string;
  tag: string;
  icon?: string | null;
  lobbyNumber: number;
  captainId: string;
  members: ArenaAssemblyMember[];
}

export interface ArenaAssembly {
  tournamentId: string;
  state: "NotAssembled" | "Preview" | "Confirmed";
  lobbyCount: number;
  teams: ArenaAssemblyTeam[];
  unassigned: ArenaAssemblyMember[];
}

export interface ArenaLeaderboardRow {
  rank: number;
  rosterId: string;
  name: string;
  tag: string;
  lobbyNumber: number;
  totalPoints: number;
  pointsByRound: Record<string, number>;
  members: ArenaAssemblyMember[];
}

export interface ArenaLeaderboard {
  tournamentId: string;
  rounds: number;
  currentRound: number;
  lobbyCount: number;
  tasksDocUrl?: string | null;
  rows: ArenaLeaderboardRow[];
}

// ── API ──────────────────────────────────────────────────────────────────────

export const arenaApi = {
  getSeries: (tournamentId: string) =>
    api
      .get<ArenaSeries>(`/arena/tournaments/${tournamentId}/series`)
      .then((r) => r.data),

  getStandings: (tournamentId: string) =>
    api
      .get<ArenaStanding[]>(`/arena/tournaments/${tournamentId}/standings`)
      .then((r) => r.data),

  startSeries: (tournamentId: string) =>
    api
      .post<ArenaSeries>(`/arena/tournaments/${tournamentId}/start-series`)
      .then((r) => r.data),

  submitResult: (
    gameId: string,
    placements: ArenaPlacementInput[],
    riotGameId?: string,
  ) =>
    api
      .post<ArenaSeries>(`/arena/games/${gameId}/result`, {
        placements,
        riotGameId: riotGameId ?? null,
      })
      .then((r) => r.data),

  /** «Закончили игру N» — серверная подтяжка результата из истории Riot (без приложения). */
  fetchResult: (gameId: string) =>
    api
      .post<ArenaSeries>(`/arena/games/${gameId}/fetch-result`)
      .then((r) => r.data),

  // ── Arena Random: сборка ──────────────────────────────────────────────────

  getAssembly: (tournamentId: string) =>
    api
      .get<ArenaAssembly>(`/arena/tournaments/${tournamentId}/assembly`)
      .then((r) => r.data),

  assemble: (tournamentId: string) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/assemble`)
      .then((r) => r.data),

  swapMembers: (tournamentId: string, userIdA: string, userIdB: string) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/swap`, {
        userIdA,
        userIdB,
      })
      .then((r) => r.data),

  setCaptain: (tournamentId: string, rosterId: string, userId: string) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/captain`, {
        rosterId,
        userId,
      })
      .then((r) => r.data),

  moveTeamLobby: (tournamentId: string, rosterId: string, lobby: number) =>
    api
      .post<ArenaAssembly>(
        `/arena/tournaments/${tournamentId}/teams/${rosterId}/lobby`,
        { lobby },
      )
      .then((r) => r.data),

  confirmTeams: (tournamentId: string) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/confirm-teams`)
      .then((r) => r.data),

  // ── Arena Random: баллы + лидерборд ───────────────────────────────────────

  getLeaderboard: (tournamentId: string) =>
    api
      .get<ArenaLeaderboard>(`/arena/tournaments/${tournamentId}/leaderboard`)
      .then((r) => r.data),

  setScore: (tournamentId: string, rosterId: string, round: number, points: number) =>
    api
      .post<ArenaLeaderboard>(`/arena/tournaments/${tournamentId}/score`, {
        rosterId,
        round,
        points,
      })
      .then((r) => r.data),

  setCurrentRound: (tournamentId: string, round: number) =>
    api
      .post<ArenaLeaderboard>(`/arena/tournaments/${tournamentId}/current-round`, {
        round,
      })
      .then((r) => r.data),

  // ── Arena Random: управление составами (замены) ───────────────────────────

  seatMember: (tournamentId: string, rosterId: string, userId: string) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/seat`, {
        rosterId,
        userId,
      })
      .then((r) => r.data),

  removeMember: (
    tournamentId: string,
    rosterId: string,
    userId: string,
    mode: "bench" | "tournament",
  ) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/remove-member`, {
        rosterId,
        userId,
        mode,
      })
      .then((r) => r.data),

  replaceMember: (
    tournamentId: string,
    rosterId: string,
    outUserId: string,
    inUserId: string,
  ) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/replace-member`, {
        rosterId,
        outUserId,
        inUserId,
      })
      .then((r) => r.data),

  disbandRoster: (tournamentId: string, rosterId: string) =>
    api
      .post<ArenaAssembly>(`/arena/tournaments/${tournamentId}/disband`, {
        rosterId,
      })
      .then((r) => r.data),

  getRosterInvite: (tournamentId: string, rosterId: string) =>
    api
      .get<ArenaRosterInvite>(
        `/arena/tournaments/${tournamentId}/teams/${rosterId}/invite`,
      )
      .then((r) => r.data),

  reissueRosterInvite: (tournamentId: string, rosterId: string) =>
    api
      .post<ArenaRosterInvite>(
        `/arena/tournaments/${tournamentId}/teams/${rosterId}/invite/reissue`,
      )
      .then((r) => r.data),

  joinByInvite: (token: string) =>
    api
      .post<ArenaAssembly>(`/arena/invite/${token}/join`)
      .then((r) => r.data),

  getRostersAudit: (tournamentId: string) =>
    api
      .get<ArenaRosterAudit[]>(
        `/arena/tournaments/${tournamentId}/rosters/audit`,
      )
      .then((r) => r.data),
};
